import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";

/** The boxes page's tabs: every box, the ones added lately, and the shopper's saved ones. */
export const BOX_TABS = ["all", "new", "favorites"] as const;
export type BoxTab = (typeof BOX_TABS)[number];

/** How the grid is ordered; `default` is the API's own order, newest first. */
export const BOX_SORTS = ["default", "price-asc", "price-desc", "popular"] as const;
export type BoxSort = (typeof BOX_SORTS)[number];

/**
 * The boxes page's filters as its URL carries them, so a filtered view survives opening a box and
 * coming back. A default is left out of the URL rather than written into it.
 */
export interface BoxesSearch {
  tab?: Exclude<BoxTab, "all">;
  q?: string;
  /** Price bounds, in cents. */
  min?: number;
  max?: number;
  sort?: Exclude<BoxSort, "default">;
  /** Only boxes the shopper's balance covers. */
  enough?: true;
}

function isMember<T extends string>(set: readonly T[], value: unknown): value is T {
  return typeof value === "string" && (set as readonly string[]).includes(value);
}

function cents(value: unknown): number | undefined {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isSafeInteger(n) && n >= 0 ? n : undefined;
}

/** The route's `validateSearch`: anything it does not recognize is dropped, never an error. */
export function parseBoxesSearch(search: Record<string, unknown>): BoxesSearch {
  const tab = isMember(BOX_TABS, search.tab) && search.tab !== "all" ? search.tab : undefined;
  const sort =
    isMember(BOX_SORTS, search.sort) && search.sort !== "default" ? search.sort : undefined;
  const q = typeof search.q === "string" && search.q !== "" ? search.q : undefined;
  return {
    ...(tab ? { tab } : {}),
    ...(q !== undefined ? { q } : {}),
    ...(cents(search.min) !== undefined ? { min: cents(search.min) } : {}),
    ...(cents(search.max) !== undefined ? { max: cents(search.max) } : {}),
    ...(sort ? { sort } : {}),
    ...(search.enough === true || search.enough === "true" ? { enough: true as const } : {}),
  };
}

/** The boxes page's filters, and a writer that replaces the URL's entry rather than adding one. */
export function useBoxesSearch(): [BoxesSearch, (patch: Partial<BoxesSearch>) => void] {
  const search = useSearch({ from: "/boxes" });
  const navigate = useNavigate({ from: "/boxes" });
  const update = useCallback(
    (patch: Partial<BoxesSearch>) => {
      void navigate({
        search: (prev) => parseBoxesSearch({ ...prev, ...patch }),
        replace: true,
      });
    },
    [navigate],
  );
  return [search, update];
}
